import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  Typography,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useProjectStore } from '../store/projectStore';
import { useIssueStore } from '../store/issueStore';
import { ProjectFormDialog } from '../components/ProjectFormDialog';
import { DeleteConfirmationDialog } from '../components/DeleteConfirmationDialog';
import { Project } from '../types/project';
import { format, getYear } from 'date-fns';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { useAuthStore } from '../store/authStore';
import { isAdmin } from '../utils/permissions';
import { projectUserApi } from '../api/projectUserApi';

type Order = 'asc' | 'desc';
type OrderBy = 'code' | 'name' | 'created_on' | 'status' | 'issues_count';

interface HeadCell {
  id: OrderBy | 'description' | 'actions' | 'users';
  label: string;
  sortable: boolean;
}

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: OrderBy) => void;
  order: Order;
  orderBy: OrderBy;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort } = props;
  const headCells: HeadCell[] = [
    { id: 'code', label: 'Event Code', sortable: true },
    { id: 'name', label: 'Name', sortable: true },
    { id: 'description', label: 'Description', sortable: false },
    { id: 'created_on', label: 'Year Creation', sortable: true },
    { id: 'issues_count', label: 'No. Ebira issued', sortable: true },
    { id: 'actions', label: 'Actions', sortable: false },
  ];

  const createSortHandler = (property: OrderBy) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  const cellBorder = {
    border: '1px solid #e8e8e8',
    backgroundColor: '#e0e0e0',  
  };

  return (
    <TableHead
      sx={{
        backgroundColor: '#e0e0e0',
        position: 'sticky',
        top: 0,
        zIndex: 1,
      }}
    >
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={cellBorder}
          >
            {headCell.sortable ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id as OrderBy)}
              >
                {headCell.label}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export const Projects: React.FC = () => {
  const navigate = useNavigate();
  const {
    projects,
    isLoading,
    error,
    fetchProjects,
    createProject,
    updateProject,
    deleteProject,
    order,
    orderBy,
    search,
    year,
    setOrder,
    setOrderBy,
    setSearch,
    setYear,
  } = useProjectStore();
  const { issues, loading: issuesLoading, error: issuesError, fetchIssues } = useIssueStore();
  const { user } = useAuthStore(); // Get the current user from auth store

  const [formDialogOpen, setFormDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedProject, setSelectedProject] = React.useState<Project | undefined>(undefined);
  const [searchInput, setSearchInput] = React.useState('');
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
  const [userProjectIds, setUserProjectIds] = React.useState<number[]>([]);
  
  // Pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  React.useEffect(() => {
    setSearchInput(search);
  }, [search]);

  // Check if user has admin permissions
  const hasAdminPermission = isAdmin(user);

  // Fetch user's projects (skip for admins/super users)
  React.useEffect(() => {
    if (!user?.id) {
      return;
    }

    if (hasAdminPermission) {
      setUserProjectIds([]);
      return;
    }

    const fetchUserProjects = async () => {
      try {
        const projectIds = await projectUserApi.getUserProjects(user.id);
        setUserProjectIds(projectIds);
      } catch (error) {
        console.error('Failed to fetch user projects:', error);
      }
    };

    fetchUserProjects();
  }, [user?.id, hasAdminPermission]);

  // Initial fetch
  React.useEffect(() => {
    fetchProjects();
    fetchIssues({});
  }, [fetchProjects, fetchIssues]);

  // Fetch when backend query parameters change
  React.useEffect(() => {
    fetchProjects();
  }, [order, orderBy, search, year, fetchProjects]);

  const commitSearch = React.useCallback(() => {
    const normalized = searchInput.trim();
    if (normalized !== search) {
      setSearch(normalized);
      setPage(0);
    }
  }, [searchInput, search, setSearch]);

  // Get unique years from project creation dates
  const years = React.useMemo(() => {
    if (!Array.isArray(projects)) {
      console.error('Projects is not an array:', projects);
      return [];
    }
    const uniqueYears = new Set(
      projects.map(project => getYear(new Date(project.created_on)))
    );
    return Array.from(uniqueYears).sort((a, b) => b - a);
  }, [projects]);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateClick = () => {
    setSelectedProject(undefined);
    setFormDialogOpen(true);
  };

  const handleEditClick = (project: Project) => {
    setSelectedProject(project);
    setFormDialogOpen(true);
  };

  const handleDeleteClick = (project: Project) => {
    setSelectedProject(project);
    setDeleteDialogOpen(true);
  };

  const handleFormSubmit = async (data: any) => {
    if (selectedProject) {
      await updateProject(selectedProject.id, data);
      setSuccessMessage('Event updated successfully');
    } else {
      await createProject(data);
      setSuccessMessage('Event created successfully');
    }
    setFormDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (selectedProject) {
      await deleteProject(selectedProject.id);
      setSelectedProject(undefined);
      setDeleteDialogOpen(false);
    }
  };

  const handleUsersClick = () => {
    navigate(`/users`);
  };

  // Filter projects to show only those the user is mapped to
  const filteredProjects = React.useMemo(() => {
    if (hasAdminPermission) {
      return projects;
    }
    return projects.filter(project => userProjectIds.includes(project.id));
  }, [projects, userProjectIds, hasAdminPermission]);
  
  // Pagination slice
  const paginatedProjects = React.useMemo(() => (
    filteredProjects.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    )
  ), [filteredProjects, page, rowsPerPage]);

  // Custome styles
  const customTdWidth120 = { width: "100px"};
  const cellBorder = { border: 'thin solid #e8e8e8' };

  const highlightMatch = (text: string, query: string): React.ReactNode => {
    if (!query) return text;

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const index = lowerText.indexOf(lowerQuery);

    if (index === -1) return text;

    const before = text.slice(0, index);
    const match = text.slice(index, index + query.length);
    const after = text.slice(index + query.length);

    return (
      <>
        {before}
        <Box component="span" sx={{ fontWeight: 'bold' }}>
          {match}
        </Box>
        {after}
      </>
    );
  };

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs items={[{ label: 'Events' }]} />

      {/* Main Content */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Left Side */}
        <Grid item xs={12} sm={12}>
          {/* Left Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start'}}>
              <Typography variant="h5" component="h1">
                Events
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end'}}>
              {hasAdminPermission && (
                <>
                  <Button
                    variant="contained"
                    onClick={handleUsersClick}
                    sx={{ mr: 2 }}
                  >
                    User Settings
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateClick}
                  >
                    Create Event
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={2}>
              <TextField
                select
                fullWidth
                label="Sort By"
                variant="outlined"
                size="small"
                value={orderBy}
                sx={{
                  '& .MuiSelect-select': {
                    // condition: make the selected value bold only when you want
                    fontWeight: orderBy ? 'bold' : 'normal',
                    // or e.g. only bold when "code" is selected:
                    // fontWeight: orderBy === 'code' ? 'bold' : 'normal',
                  },
                }}
                onChange={(e) => {
                  setOrderBy(e.target.value as OrderBy);
                }}
              >
                <MenuItem value="code">Code</MenuItem>
                <MenuItem value="name">Name</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                select
                fullWidth
                label="Order"
                variant="outlined"
                size="small"
                value={order}
                sx={{
                  '& .MuiSelect-select': {
                    // condition: make the selected value bold only when you want
                    fontWeight: order ? 'bold' : 'normal',
                    // or e.g. only bold when "code" is selected:
                    // fontWeight: orderBy === 'code' ? 'bold' : 'normal',
                  },
                }}
                onChange={(e) => {
                  setOrder(e.target.value as Order);
                }}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Search"
                variant="outlined"
                size="small"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onBlur={commitSearch}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Tab') {
                    commitSearch();
                  }
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontWeight: searchInput ? 'bold' : 'normal',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                label="Year"
                variant="outlined"
                size="small"
                value={year}
                sx={{
                  '& .MuiSelect-select': {
                    // fontWeight: filters.year !== 'all' ? 'bold' : 'normal',
                    fontWeight: 'bold'
                  },
                }}
                onChange={(e) => {
                  setYear(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">All Years</MenuItem>
                {years.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {/* Events Table */}
          <Paper>
            <TableContainer
              sx={{
                maxHeight: '60vh',
                overflowY: 'auto',
              }}
            >
              <Table stickyHeader>
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {paginatedProjects.map((project) => (
                    <TableRow
                      hover
                      key={project.id}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/projects/${project.id}`)}
                    >
                      <TableCell sx={cellBorder}>{highlightMatch(project.code, search)}</TableCell>
                      <TableCell sx={cellBorder}>{highlightMatch(project.name, search)}</TableCell>
                      <TableCell sx={cellBorder}>{highlightMatch(project.description || '', search)}</TableCell>
                      <TableCell sx={cellBorder}>
                        {format(new Date(project.created_on), 'yyyy')}
                      </TableCell>
                      <TableCell sx={cellBorder}>
                        {project.issues_count ?? issues.filter(issue => issue.project_id === project.id).length}
                      </TableCell>
                      <TableCell sx={{ ...cellBorder, width: '85px' }}>
                        {hasAdminPermission && (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditClick(project);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(project);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredProjects.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Grid> 
      </Grid>

      {/* Dialogs */}
      <ProjectFormDialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        onSubmit={handleFormSubmit}
        project={selectedProject}
        onSuccess={(message) => setSuccessMessage(message)}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Event"
        message={`Are you sure you want to delete project "${selectedProject?.name}"? This action cannot be undone.`}
        eventCode={selectedProject?.code}
      />

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
